const { fitModel, entryFor, variantFor, chooseFit } = await import(document.querySelector('script[data-pdp-options-src]')?.dataset.pdpOptionsSrc || new URL('./dirtlej-pdp-options.js',import.meta.url).href).catch(()=>({}));
/* No cart requests, stock assumptions or price copies. Pending fit/size choices are guarded before submission.
   Strong owns buying and the existing size guide. */
const ROOT = '.dlj-pdp-main';
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Product-data adapter for the existing feature layout: keep every explanation
// readable and show the photograph belonging to the focused/selected row.
const featureControllers = new Map();
function initialiseFeatures(root) {
  if (featureControllers.has(root)) return;
  const rows = [...root.querySelectorAll('[data-dlj-feature-item]')];
  const photos = [...root.querySelectorAll('[data-pdp-feature-photo]')];
  if (!rows.length || rows.length !== photos.length) return;
  const abort = new AbortController();
  const activate = index => {
    rows.forEach((row,position) => {
      const selected = position === index;
      row.classList.toggle('is-active',selected);
      row.querySelector('[data-pdp-feature-select]')?.setAttribute('aria-pressed',String(selected));
      photos[position].hidden = !selected;
      if (!selected) photos[position].querySelectorAll('video').forEach(video => video.pause());
    });
  };
  rows.forEach((row,index) => {
    const link = row.querySelector('[data-pdp-feature-select]');
    if (!link) return;
    link.setAttribute('role','button');
    link.setAttribute('aria-controls',photos[index].id);
    link.addEventListener('click',event => { event.preventDefault(); activate(index); },{ signal:abort.signal });
    link.addEventListener('focus',() => activate(index),{ signal:abort.signal });
    row.addEventListener('mouseenter',() => {
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) activate(index);
    },{ signal:abort.signal });
    link.addEventListener('keydown',event => {
      if (event.key === ' ') { event.preventDefault(); activate(index); return; }
      let next;
      if (event.key === 'ArrowDown') next = (index + 1) % rows.length;
      if (event.key === 'ArrowUp') next = (index + rows.length - 1) % rows.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = rows.length - 1;
      if (next === undefined) return;
      event.preventDefault();
      rows[next].querySelector('[data-pdp-feature-select]')?.focus({ preventScroll:true });
    },{ signal:abort.signal });
  });
  root.addEventListener('shopify:block:select',event => {
    const index = rows.indexOf(event.target.closest('[data-dlj-feature-item]'));
    if (index >= 0) activate(index);
  },{ signal:abort.signal });
  root.classList.add('is-enhanced');
  activate(0);
  featureControllers.set(root,abort);
}

const sizeControllers = new Map();
function initialiseSizes(root) {
  if (typeof fitModel!=='function' || sizeControllers.has(root)) return;
  let summary = root.querySelector('.product-summary');
  const labels = root.querySelector('[data-pdp-choice-labels]')?.dataset;
  if (!summary || !labels) return;
  const abort = new AbortController();
  const fields = new Map();
  const pendingIntents = new Map();
  const remember = (field,controller) => { const state=controller.snapshot(); if(state.pending)pendingIntents.set(field.dataset.pdpOptionIndex,state); };
  const readVariants = () => {
    try { return JSON.parse(summary.querySelector('[data-product-variants]')?.textContent || '[]'); }
    catch { return []; }
  };
  const valuesOf = field => [...field.querySelectorAll('input.field-value[type="radio"],select.field-value option')].map(input=>input.value);
  const selectedOf = field => field.querySelector('input.field-value:checked')?.value ?? field.querySelector('select.field-value')?.value ?? null;
  const make = (tag,cls,text) => { const node=document.createElement(tag); if(cls)node.className=cls; if(text)node.textContent=text; return node; };
  function attach(field,model,signature) {
    const index=Number(field.dataset.pdpOptionIndex);
    const native=field.querySelector('.field-wrap');
    const title=field.querySelector('.field-title');
    if (!native || !title) return;
    const local = new AbortController();
    const guide=title.querySelector('.size-guide-trigger');
    const guideParent=guide?.parentNode;
    const nativeHidden=native.hidden, titleHidden=title.hidden;
    const ui=make('div','dlj-pdp__size-picker');
    ui.setAttribute('data-pdp-size-picker','');
    const header=make('div','dlj-pdp__choice-heading');
    const sizeLabel=make('span','',labels.size);
    sizeLabel.id='PdpSize-'+root.id+'-'+index;
    const chosen=make('span','dlj-pdp__chosen-size');
    const selection=make('span','dlj-pdp__size-selection');
    selection.append(sizeLabel,chosen);header.append(selection);if(guide)header.append(guide);
    const sizes=make('div','dlj-pdp__size-groups');
    const status=make('p','dlj-pdp__size-status');
    status.setAttribute('role','status');status.setAttribute('aria-live','polite');
    const groups=new Map(), sizeButtons=new Map();
    let current=model.entries.find(entry=>entry.value===selectedOf(field));
    // Preserve deep-linked selections. Do not silently select a different size.
    const intent=pendingIntents.get(field.dataset.pdpOptionIndex);
    let state=intent || (current ? {...current,pending:false} : {fit:'regular',base:null,value:null,pending:true});
    pendingIntents.delete(field.dataset.pdpOptionIndex);
    const guarded=new Map(), express=new Map(), stockDisabled=new Set();
    const originalOptions=[...summary.querySelectorAll('[data-pdp-option-index]')].map(selectedOf);
    const originalVariant=current ? variantFor(readVariants(),index,current.value,originalOptions) : null;
    if(originalVariant?.available===false)summary.querySelectorAll('button[name="add"]').forEach(button=>{if(button.disabled)stockDisabled.add(button);});
    function syncStockDisabled(selected) {
      if(state.pending||!selected)return;
      summary.querySelectorAll('button[type="submit"][name="add"]').forEach(button=>{
        if(selected.available===false){if(!button.disabled){button.disabled=true;stockDisabled.add(button);}return;}
        if(!stockDisabled.has(button))return;
        const invalidRequired=[...summary.querySelectorAll('input[required],.email-input')].some(input=>!input.value?.trim()||(input.validity&&!input.validity.valid));
        if(!invalidRequired&&!button.classList.contains('disabled')&&!button.classList.contains('selling-plan-error')&&!button.closest('form')?.classList.contains('disabled')){
          button.disabled=false;stockDisabled.delete(button);
        }
      });
    }
    function guard() {
      const pending=state.pending;
      ui.dataset.pending=String(pending);
      if(pending) {
        summary.querySelectorAll('button[type="submit"][name="add"]').forEach(button=>{
          if(!guarded.has(button))guarded.set(button,button.disabled);
          if(!button.disabled)button.disabled=true;
        });
        summary.querySelectorAll('.shopify-payment-button').forEach(host=>{
          if(!express.has(host))express.set(host,{hidden:host.hidden,inert:host.inert});
          if(!host.hidden)host.hidden=true;
          host.inert=true;
        });
      } else {
        guarded.forEach((disabled,button)=>{button.disabled=disabled;});guarded.clear();
        express.forEach((old,host)=>{host.hidden=old.hidden;host.inert=old.inert;});express.clear();
      }
    }
    function refresh() {
      const other=[...summary.querySelectorAll('[data-pdp-option-index]')].map(selectedOf);
      const variants=readVariants();
      const otherComplete=other.every((value,i)=>i===index||value!=null);
      if(!state.pending&&otherComplete&&!variantFor(variants,index,state.value,other))state={fit:state.fit,base:null,value:null,pending:true};
      sizeButtons.forEach((button,value)=>{
        const variant=variantFor(variants,index,value,other);
        const offered=otherComplete ? Boolean(variant) : variants.some(candidate=>{
          const options=candidate.options || [candidate.option1,candidate.option2,candidate.option3];
          return options[index]===value && other.every((selected,i)=>i===index||selected==null||options[i]===selected);
        });
        button.hidden=!offered;button.disabled=!offered;
        button.setAttribute('aria-pressed',String(state.value===value && !state.pending));
        button.dataset.stock=variant ? String(variant.available) : 'unknown';
        const note=variant?.available===false ? labels.soldOut : '';
        button.setAttribute('aria-label',value+(note ? ' — '+note : ''));
      });
      groups.forEach(group=>{
        const visible=[...group.querySelectorAll('[data-pdp-size]')].filter(button=>!button.hidden);
        group.hidden=!visible.length;
        group.querySelector('.dlj-pdp__size-choices').style.setProperty('--pdp-size-columns',String(Math.min(5,visible.length)||1));
      });
      chosen.textContent=state.pending ? labels.choose : state.value;
      const selected=state.value ? variantFor(variants,index,state.value,other) : null;
      status.textContent=state.pending ? labels.choose : selected?.available===false ? labels.soldOut : '';
      guard();
      syncStockDisabled(selected);
    }
    function commit() {
      const other=[...summary.querySelectorAll('[data-pdp-option-index]')].map(selectedOf);
      if(!state.pending && other.every((value,i)=>i===index||value!=null) && !variantFor(readVariants(),index,state.value,other))state={fit:state.fit,base:null,value:null,pending:true};
      refresh();
      if(state.pending)return;
      const radios=[...native.querySelectorAll('input.field-value[type="radio"]')];
      const select=native.querySelector('select.field-value');
      let target;
      if(radios.length){radios.forEach(input=>input.checked=input.value===state.value);target=radios.find(input=>input.checked);}
      else if(select){select.value=state.value;target=select;}
      // Keep the original native value and event path responsible for variant/cart updates.
      target?.dispatchEvent(new Event('change',{bubbles:true}));
    }
    for(const fit of ['regular','loose']) {
      const group=make('div','dlj-pdp__size-group');group.dataset.pdpFitGroup=fit;
      const label=make('span','dlj-pdp__fit-label',labels[fit]);label.id='PdpFit-'+root.id+'-'+index+'-'+fit;
      const heading=make('div','dlj-pdp__fit-heading');heading.append(label);
      const info=root.querySelector('[data-pdp-info-template="'+fit+'"]')?.content.querySelector('dlj-info')?.cloneNode(true);
      if(info){
        const panel=info.querySelector('.dlj-info__panel');
        panel.id+='-'+index;
        info.querySelector('summary').setAttribute('aria-controls',panel.id);
        heading.append(info);
      }
      const choices=make('div','dlj-pdp__size-choices');
      choices.setAttribute('role','group');choices.setAttribute('aria-labelledby',sizeLabel.id+' '+label.id);
      model.entries.filter(entry=>entry.fit===fit).forEach(entry=>{
        const button=make('button','',entry.value);button.type='button';button.dataset.pdpSize=entry.value;
        button.addEventListener('click',()=>{
          if(button.hidden||button.disabled)return;
          state={...entry,pending:false};commit();
        },{signal:local.signal});
        choices.append(button);sizeButtons.set(entry.value,button);
      });
      choices.addEventListener('keydown',event=>{
        const buttons=[...choices.querySelectorAll('button')].filter(button=>!button.hidden&&!button.disabled);
        const i=buttons.indexOf(event.target);if(i<0)return;let next;
        if(event.key==='ArrowRight'||event.key==='ArrowDown')next=(i+1)%buttons.length;
        if(event.key==='ArrowLeft'||event.key==='ArrowUp')next=(i+buttons.length-1)%buttons.length;
        if(event.key==='Home')next=0;if(event.key==='End')next=buttons.length-1;
        if(next===undefined)return;event.preventDefault();buttons[next].focus();buttons[next].click();
      },{signal:local.signal});
      group.append(heading,choices);sizes.append(group);groups.set(fit,group);
    }
    ui.append(header,sizes,status);field.prepend(ui);
    native.hidden=true;title.hidden=true;
    const sync=()=>{
      const other=[...summary.querySelectorAll('[data-pdp-option-index]')].map(selectedOf);
      const entry=model.entries.find(entry=>entry.value===selectedOf(field));
      // Another option (e.g. colour) can invalidate the selected size. Require a
      // real selection rather than submitting the previous colour's variant.
      if(!state.pending){
        if(entry&&variantFor(readVariants(),index,entry.value,other))state={...entry,pending:false};
        else state={fit:state.fit,base:null,value:null,pending:true};
      }
      refresh();
    };
    summary.addEventListener('change',event=>{if(event.target.matches('.field-value'))sync();},{signal:local.signal});
    summary.addEventListener('submit',event=>{
      if(!state.pending)return;event.preventDefault();event.stopImmediatePropagation();
      refresh();sizes.querySelector('button:not(:disabled)')?.focus();
    },{capture:true,signal:local.signal});
    const observer=new MutationObserver(()=>{if(state.pending)guard();});
    observer.observe(summary,{subtree:true,attributes:true,attributeFilter:['disabled']});
    const controller={signature,sync,refresh,snapshot:()=>({...state}),destroy(){
      local.abort();observer.disconnect();state.pending=false;guard();
      if(guide&&guideParent.isConnected)guideParent.append(guide);
      ui.remove();native.hidden=nativeHidden;title.hidden=titleHidden;
    }};
    fields.set(field,controller);refresh();
  }
  let queued=false;
  const scan=()=>{
    queued=false;
    if(abort.signal.aborted)return;
    const replacement=root.querySelector('.product-summary');
    if(replacement!==summary){fields.forEach((controller,field)=>{remember(field,controller);controller.destroy();});fields.clear();summary=replacement;}
    if(!summary)return;
    fields.forEach((controller,field)=>{if(!field.isConnected){remember(field,controller);controller.destroy();fields.delete(field);}});
    summary.querySelectorAll('[data-pdp-size-option]').forEach(field=>{
      const values=valuesOf(field),signature=JSON.stringify(values),old=fields.get(field);
      if(old?.signature===signature)return;
      if(old){remember(field,old);old.destroy();}fields.delete(field);
      const model=fitModel(values);if(model&&readVariants().length)attach(field,model,signature);
    });
  };
  const observer=new MutationObserver(()=>{if(!queued){queued=true;requestAnimationFrame(scan);}});
  observer.observe(root,{childList:true,subtree:true});
  document.addEventListener('product-variant-updated',()=>fields.forEach(controller=>controller.sync()),{signal:abort.signal});
  sizeControllers.set(root,{destroy(){abort.abort();observer.disconnect();fields.forEach(controller=>controller.destroy());fields.clear();}});
  scan();
}

const galleryControllers = new Map();
function initialiseGallery(root) {
  if (galleryControllers.has(root)) return;
  const gallery = root.querySelector('[data-pdp-gallery]');
  const element = gallery?.querySelector('strong-slider.product-gallery-slider');
  if (!gallery) return;
  const abort = new AbortController();
  const controller = { abort, slider: null, update: null };
  galleryControllers.set(root, controller);
  if (!element) {
    const trigger = gallery.querySelector('.media-preview-wrap[data-zoom]');
    const zoom = gallery.querySelector('[data-pdp-zoom]');
    if (zoom) {
      zoom.hidden = !trigger;
      zoom.addEventListener('click', () => trigger?.click(), { signal: abort.signal });
    }
    return;
  }
  customElements.whenDefined('strong-slider').then(() => {
    if (abort.signal.aborted || !element.isConnected) return;
    const slider = element.slider;
    if (!slider) return;
    if (reduceMotion()) slider.reInit({ duration: 0 });
    const counter = gallery.querySelector('[data-pdp-current]');
    const zoom = gallery.querySelector('[data-pdp-zoom]');
    const update = () => {
      const index = slider.selectedScrollSnap();
      if (counter) counter.textContent = String(index + 1);
      const imageButton = slider.slideNodes()[index]?.querySelector('.media-preview-wrap[data-zoom]');
      if (zoom) zoom.hidden = !imageButton;
      slider.slideNodes().forEach((slide,position) => {
        slide.inert = position !== index;
        if (position !== index) slide.querySelectorAll('video').forEach(video => video.pause());
      });
    };
    gallery.addEventListener('keydown', event => {
      if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key) || event.target.closest('video,model-viewer,input,select')) return;
      event.preventDefault();
      const last = slider.slideNodes().length - 1;
      const index = slider.selectedScrollSnap();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? last : event.key === 'ArrowRight' ? (index + 1) % (last + 1) : (index + last) % (last + 1);
      slider.scrollTo(next, reduceMotion());
    }, { signal: abort.signal });
    zoom?.addEventListener('click', () => {
      slider.slideNodes()[slider.selectedScrollSnap()]?.querySelector('.media-preview-wrap[data-zoom]')?.click();
    }, { signal: abort.signal });
    slider.on('select', update);
    slider.on('reInit', update);
    controller.slider = slider;
    controller.update = update;
    update();
  });
}

const chapterControllers = new Map();
function initialiseChapters(root) {
  if (chapterControllers.has(root)) return;
  const targets = {
    overview: document.querySelector('.dlj-pdp-main'),
    features: document.querySelector('[data-pdp-features]'),
    story: document.querySelector('.dlj-pdp__story'),
    details: document.querySelector('.dlj-pdp__details'),
  };
  const pairs = [];
  root.querySelectorAll('[data-pdp-chapter]').forEach(link => {
    const target = targets[link.dataset.pdpChapter]?.closest('.shopify-section');
    link.hidden = !target;
    if (target?.id) { link.href = '#' + target.id; pairs.push([link, target]); }
  });
  root.hidden = pairs.length < 2;
  const offset = parseInt(getComputedStyle(targets.overview || root).getPropertyValue('--pdp-header-offset'), 10);
  const shell = root.closest('.dlj-pdp-nav-section');
  if (shell) shell.style.setProperty('--pdp-nav-top', `${Number.isFinite(offset) ? offset : 0}px`);
  let scheduled = false;
  const update = () => {
    scheduled = false;
    const threshold = (Number.isFinite(offset) ? offset : 0) + root.getBoundingClientRect().height + 48;
    let current = pairs[0]?.[0];
    pairs.forEach(([link,target]) => { if (target.getBoundingClientRect().top <= threshold) current = link; });
    pairs.forEach(([link]) => {
      if (link === current) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
  };
  const abort = new AbortController();
  window.addEventListener('scroll', () => {
    if (!scheduled) { scheduled = true; requestAnimationFrame(update); }
  }, { passive: true, signal: abort.signal });
  chapterControllers.set(root, abort);
  update();
}

const buyingControllers = new Map();
function initialiseBuying(root) {
  if (buyingControllers.has(root)) return;
  const bar = root.querySelector('[data-pdp-mobile-bar]');
  const returnButton = bar?.querySelector('[data-pdp-return]');
  const summary = root.querySelector('.product-summary');
  if (!bar || !returnButton || !summary) return;
  const abort = new AbortController();
  const mobile = window.matchMedia('(max-width: 991px)');
  let queued = false;
  const update = () => {
    queued = false;
    const buyButton = summary.querySelector('button[type="submit"][name="add"], .add-to-cart-button');
    const target = buyButton || summary;
    const dialogOpen = [...document.querySelectorAll('dialog[open], [aria-modal="true"]')]
      .some(dialog => dialog.getClientRects().length > 0 && getComputedStyle(dialog).visibility !== 'hidden');
    bar.hidden = !mobile.matches || target.getBoundingClientRect().bottom >= 0 || dialogOpen;
  };
  const schedule = () => {
    if (!queued) { queued = true; window.requestAnimationFrame(update); }
  };
  window.addEventListener('scroll', schedule, { passive: true, signal: abort.signal });
  window.addEventListener('resize', schedule, { passive: true, signal: abort.signal });
  document.addEventListener('click', schedule, { signal: abort.signal });
  document.addEventListener('close', schedule, { capture: true, signal: abort.signal });
  returnButton.addEventListener('click', () => {
    const clearance = parseInt(getComputedStyle(root).getPropertyValue('--pdp-header-offset'), 10) || 96;
    const top = summary.getBoundingClientRect().top + window.scrollY - clearance;
    if (!summary.hasAttribute('tabindex')) summary.setAttribute('tabindex', '-1');
    summary.focus({ preventScroll: true });
    window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion() ? 'instant' : 'smooth' });
  }, { signal: abort.signal });
  const mutationObserver = new MutationObserver(schedule);
  mutationObserver.observe(summary, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'hidden', 'open', 'aria-expanded'] });
  buyingControllers.set(root, { abort, mutationObserver });
  update();
}

function initialise(scope = document) {
  const featureRoots = [...scope.querySelectorAll('[data-pdp-features]')];
  if (scope.matches?.('[data-pdp-features]')) featureRoots.unshift(scope);
  featureRoots.forEach(initialiseFeatures);
  const buyRoots = [...scope.querySelectorAll(ROOT)];
  if (scope.matches?.(ROOT)) buyRoots.unshift(scope);
  buyRoots.forEach(root => { initialiseBuying(root); initialiseGallery(root); initialiseSizes(root); });
  document.querySelectorAll('[data-pdp-chapters]').forEach(root => {
    chapterControllers.get(root)?.abort();
    chapterControllers.delete(root);
    initialiseChapters(root);
  });
}

initialise();
document.addEventListener('shopify:section:load', event => initialise(event.target));
document.addEventListener('shopify:section:unload', event => {
  sizeControllers.forEach((controller,root)=>{
    if(event.target===root||event.target.contains(root)){controller.destroy();sizeControllers.delete(root);}
  });
  featureControllers.forEach((abort, root) => {
    if (event.target === root || event.target.contains(root)) { abort.abort(); featureControllers.delete(root); }
  });
  galleryControllers.forEach((controller, root) => {
    if (event.target === root || event.target.contains(root)) {
      controller.abort.abort();
      if (controller.slider && controller.update) {
        controller.slider.off('select', controller.update);
        controller.slider.off('reInit', controller.update);
      }
      galleryControllers.delete(root);
    }
  });
  chapterControllers.forEach((abort, root) => {
    if (event.target === root || event.target.contains(root)) { abort.abort(); chapterControllers.delete(root); }
  });
  buyingControllers.forEach((controller, root) => {
    if (event.target === root || event.target.contains(root)) {
      controller.abort.abort();
      controller.mutationObserver.disconnect();
      buyingControllers.delete(root);
    }
  });
});
